/**
 * Centralised formatters. All money in INR (Indian numbering grouping),
 * dates relative when fresh and absolute when older, durations human-readable.
 */

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const inrPaise = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatRupees(value: number, options?: { paise?: boolean }): string {
  return (options?.paise ? inrPaise : inr).format(value);
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0s";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec.toString().padStart(2, "0")}s`;
  return `${sec}s`;
}

export function formatMediaTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export function formatPercent(value: number, options?: { decimals?: number }): string {
  const decimals = options?.decimals ?? 0;
  return `${value.toFixed(decimals)}%`;
}

const dateAbsolute = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const dateSameYear = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
});

export function formatDate(input: string | number | Date): string {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  return d.getFullYear() === now.getFullYear() ? dateSameYear.format(d) : dateAbsolute.format(d);
}

export function formatRelative(input: string | number | Date): string {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);
  if (sec < 45) return "just now";
  if (min < 60) return `${min} min ago`;
  if (hr < 24) return `${hr} hr ago`;
  if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
  return formatDate(d);
}

/** Compact form for dashboard stat tiles: "3h 12m" / "12m" / "47s". */
export function formatDurationCompact(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0s";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

const weekdayFmt = new Intl.DateTimeFormat("en-IN", { weekday: "short" });
/** Three-letter weekday (Mon, Tue, ...) for chart axes. */
export function formatWeekdayShort(input: string | number | Date): string {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return weekdayFmt.format(d);
}

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

/**
 * The IST calendar date (YYYY-MM-DD) for an instant.
 *
 * The whole product buckets days in IST -- the backend does, and so must
 * anything here that has to line a chart bucket up with a backend one. The
 * naive `new Date().toISOString().slice(0, 10)` is UTC: for an IST browser
 * between midnight and 05:30 it labels "today" with yesterday's key, silently
 * shifting the chart a column off from the numbers beside it.
 */
export function istDateKey(input: string | number | Date = Date.now()): string {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return new Date(d.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * Parse a `YYYY-MM-DD` IST date key back to the Date at noon IST on that day.
 * Noon, not midnight, so a weekday label can't be tipped onto the wrong day by
 * the viewer's own timezone offset.
 */
export function istDateKeyToDate(dateKey: string): Date {
  return new Date(new Date(`${dateKey}T12:00:00.000Z`).getTime() - IST_OFFSET_MS);
}

/**
 * First name from a full name string. Falls back to "you" so we never
 * say "User" or "Student" or anything that breaks the human voice.
 * Trims, splits on whitespace, takes first word, title-cases first
 * letter only (preserves rest as-typed -- handles 'McDonald' etc.).
 */
export function firstName(fullName: string | null | undefined): string {
  if (!fullName) return "you";
  const trimmed = fullName.trim();
  if (!trimmed) return "you";
  const first = trimmed.split(/\s+/)[0];
  if (!first) return "you";
  return first.charAt(0).toUpperCase() + first.slice(1);
}
